//src/hooks/useQuestionnaires.ts
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import {
  cloneQuestionnaireVersion,
  createQuestionnaire,
  createQuestionnaireOption,
  createQuestionnaireQuestion,
  createQuestionnaireSection,
  createQuestionnaireVersion,
  deleteQuestionnaireOption,
  deleteQuestionnaireQuestion,
  deleteQuestionnaireSection,
  getQuestionnaire,
  getQuestionnaireVersion,
  listQuestionnaires,
  listQuestionnaireVersions,
  publishQuestionnaireVersion,
  updateQuestionnaire,
  updateQuestionnaireOption,
  updateQuestionnaireQuestion,
  updateQuestionnaireSection,
} from "@/services/questionnaires";
import type {
  QuestionnaireCloneVersionIn,
  QuestionnaireCreateIn,
  QuestionnaireOptionUpsertIn,
  QuestionnaireQuestionUpsertIn,
  QuestionnaireSectionUpsertIn,
  QuestionnaireUpdateIn,
  QuestionnaireVersionCreateIn,
} from "@/types/gro";

export function useQuestionnaires(params?: {
  include_inactive?: boolean;
  q?: string;
}) {
  return useQuery({
    queryKey: ["questionnaires", params?.include_inactive ?? false, params?.q ?? ""],
    queryFn: () => listQuestionnaires(params),
  });
}

export function useQuestionnaireDetail(id?: string) {
  return useQuery({
    queryKey: ["questionnaire_detail", id],
    queryFn: () => getQuestionnaire(id as string),
    enabled: !!id,
  });
}

export function useQuestionnaireVersions(questionnaireId?: string) {
  return useQuery({
    queryKey: ["questionnaire_versions", questionnaireId],
    queryFn: () => listQuestionnaireVersions(questionnaireId as string),
    enabled: !!questionnaireId,
  });
}

export function useQuestionnaireVersionDetail(versionId?: string) {
  return useQuery({
    queryKey: ["questionnaire_version_detail", versionId],
    queryFn: () => getQuestionnaireVersion(versionId as string),
    enabled: !!versionId,
  });
}

export function useCreateQuestionnaire() {
  const qc = useQueryClient();

  return useMutation({
    mutationFn: (body: QuestionnaireCreateIn) => createQuestionnaire(body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["questionnaires"] });
    },
  });
}

export function useUpdateQuestionnaire() {
  const qc = useQueryClient();

  return useMutation({
    mutationFn: ({
      id,
      body,
    }: {
      id: string;
      body: QuestionnaireUpdateIn;
    }) => updateQuestionnaire(id, body),
    onSuccess: (_data, variables) => {
      qc.invalidateQueries({ queryKey: ["questionnaires"] });
      qc.invalidateQueries({
        queryKey: ["questionnaire_detail", variables.id],
      });
    },
  });
}

export function useCreateQuestionnaireVersion() {
  const qc = useQueryClient();

  return useMutation({
    mutationFn: ({
      questionnaireId,
      body,
    }: {
      questionnaireId: string;
      body: QuestionnaireVersionCreateIn;
    }) => createQuestionnaireVersion(questionnaireId, body),
    onSuccess: (_data, variables) => {
      qc.invalidateQueries({
        queryKey: ["questionnaire_versions", variables.questionnaireId],
      });
      qc.invalidateQueries({
        queryKey: ["questionnaire_detail", variables.questionnaireId],
      });
      qc.invalidateQueries({ queryKey: ["questionnaires"] });
    },
  });
}

export function useCloneQuestionnaireVersion() {
  const qc = useQueryClient();

  return useMutation({
    mutationFn: ({
      versionId,
      questionnaireId,
      body,
    }: {
      versionId: string;
      questionnaireId: string;
      body: QuestionnaireCloneVersionIn;
    }) => cloneQuestionnaireVersion(versionId, body),
    onSuccess: (_data, variables) => {
      qc.invalidateQueries({
        queryKey: ["questionnaire_versions", variables.questionnaireId],
      });
      qc.invalidateQueries({
        queryKey: ["questionnaire_detail", variables.questionnaireId],
      });
      qc.invalidateQueries({ queryKey: ["questionnaires"] });
    },
  });
}

export function usePublishQuestionnaireVersion() {
  const qc = useQueryClient();

  return useMutation({
    mutationFn: ({
      versionId,
      questionnaireId,
    }: {
      versionId: string;
      questionnaireId: string;
    }) => publishQuestionnaireVersion(versionId),
    onSuccess: (_data, variables) => {
      qc.invalidateQueries({
        queryKey: ["questionnaire_versions", variables.questionnaireId],
      });
      qc.invalidateQueries({
        queryKey: ["questionnaire_version_detail", variables.versionId],
      });
      qc.invalidateQueries({
        queryKey: ["questionnaire_detail", variables.questionnaireId],
      });
      qc.invalidateQueries({ queryKey: ["questionnaires"] });
    },
  });
}

export function useCreateQuestionnaireSection() {
  const qc = useQueryClient();

  return useMutation({
    mutationFn: ({
      versionId,
      body,
    }: {
      versionId: string;
      body: QuestionnaireSectionUpsertIn;
    }) => createQuestionnaireSection(versionId, body),
    onSuccess: (_data, variables) => {
      qc.invalidateQueries({
        queryKey: ["questionnaire_version_detail", variables.versionId],
      });
    },
  });
}

export function useUpdateQuestionnaireSection() {
  const qc = useQueryClient();

  return useMutation({
    mutationFn: ({
      versionId,
      sectionId,
      body,
    }: {
      versionId: string;
      sectionId: string;
      body: QuestionnaireSectionUpsertIn;
    }) => updateQuestionnaireSection(sectionId, body),
    onSuccess: (_data, variables) => {
      qc.invalidateQueries({
        queryKey: ["questionnaire_version_detail", variables.versionId],
      });
    },
  });
}

export function useDeleteQuestionnaireSection() {
  const qc = useQueryClient();

  return useMutation({
    mutationFn: ({
      versionId,
      sectionId,
    }: {
      versionId: string;
      sectionId: string;
    }) => deleteQuestionnaireSection(sectionId),
    onSuccess: (_data, variables) => {
      qc.invalidateQueries({
        queryKey: ["questionnaire_version_detail", variables.versionId],
      });
    },
  });
}

export function useCreateQuestionnaireQuestion() {
  const qc = useQueryClient();

  return useMutation({
    mutationFn: ({
      versionId,
      body,
    }: {
      versionId: string;
      body: QuestionnaireQuestionUpsertIn;
    }) => createQuestionnaireQuestion(versionId, body),
    onSuccess: (_data, variables) => {
      qc.invalidateQueries({
        queryKey: ["questionnaire_version_detail", variables.versionId],
      });
    },
  });
}

export function useUpdateQuestionnaireQuestion() {
  const qc = useQueryClient();

  return useMutation({
    mutationFn: ({
      versionId,
      questionId,
      body,
    }: {
      versionId: string;
      questionId: string;
      body: QuestionnaireQuestionUpsertIn;
    }) => updateQuestionnaireQuestion(questionId, body),
    onSuccess: (_data, variables) => {
      qc.invalidateQueries({
        queryKey: ["questionnaire_version_detail", variables.versionId],
      });
    },
  });
}

export function useDeleteQuestionnaireQuestion() {
  const qc = useQueryClient();

  return useMutation({
    mutationFn: ({
      versionId,
      questionId,
    }: {
      versionId: string;
      questionId: string;
    }) => deleteQuestionnaireQuestion(questionId),
    onSuccess: (_data, variables) => {
      qc.invalidateQueries({
        queryKey: ["questionnaire_version_detail", variables.versionId],
      });
    },
  });
}

export function useCreateQuestionnaireOption() {
  const qc = useQueryClient();

  return useMutation({
    mutationFn: ({
      questionId,
      versionId,
      body,
    }: {
      questionId: string;
      versionId: string;
      body: QuestionnaireOptionUpsertIn;
    }) => createQuestionnaireOption(questionId, body),
    onSuccess: (_data, variables) => {
      qc.invalidateQueries({
        queryKey: ["questionnaire_version_detail", variables.versionId],
      });
    },
  });
}

export function useUpdateQuestionnaireOption() {
  const qc = useQueryClient();

  return useMutation({
    mutationFn: ({
      optionId,
      versionId,
      body,
    }: {
      optionId: string;
      versionId: string;
      body: QuestionnaireOptionUpsertIn;
    }) => updateQuestionnaireOption(optionId, body),
    onSuccess: (_data, variables) => {
      qc.invalidateQueries({
        queryKey: ["questionnaire_version_detail", variables.versionId],
      });
    },
  });
}

export function useDeleteQuestionnaireOption() {
  const qc = useQueryClient();

  return useMutation({
    mutationFn: ({
      optionId,
      versionId,
    }: {
      optionId: string;
      versionId: string;
    }) => deleteQuestionnaireOption(optionId),
    onSuccess: (_data, variables) => {
      qc.invalidateQueries({
        queryKey: ["questionnaire_version_detail", variables.versionId],
      });
    },
  });
}
